import type { User } from 'firebase/auth';
import { get, ref, set, update } from 'firebase/database';

import { database } from './firebase';

type SocialProfile = {
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  email?: string | null;
  photoURL?: string | null;
};

export async function ensureUserProfile(user: User, profile?: SocialProfile): Promise<void> {
  try {
    const userRef = ref(database, `users/${user.uid}`);
    const snapshot = await get(userRef);
    const now = Date.now();

    const normalized = {
      email: profile?.email ?? user.email ?? null,
      firstName: profile?.firstName ?? null,
      lastName: profile?.lastName ?? null,
      displayName: profile?.displayName ?? user.displayName ?? null,
      photoURL: profile?.photoURL ?? user.photoURL ?? null,
    };

    if (!snapshot.exists()) {
      await set(userRef, {
        uid: user.uid,
        ...normalized,
        createdAt: now,
        updatedAt: now,
        needsOrganizationSetup: true,
      });
      return;
    }

    const existing = snapshot.val() ?? {};
    const updates: Record<string, unknown> = { updatedAt: now };

    if (!existing.email && normalized.email) {
      updates.email = normalized.email;
    }
    if (!existing.firstName && normalized.firstName) {
      updates.firstName = normalized.firstName;
    }
    if (!existing.lastName && normalized.lastName) {
      updates.lastName = normalized.lastName;
    }
    if (!existing.displayName && normalized.displayName) {
      updates.displayName = normalized.displayName;
    }
    if (!existing.photoURL && normalized.photoURL) {
      updates.photoURL = normalized.photoURL;
    }
    if (existing.needsOrganizationSetup === undefined) {
      updates.needsOrganizationSetup = true;
    }

    if (Object.keys(updates).length > 1) {
      await update(userRef, updates);
    }
  } catch (error) {
    console.warn('Failed to upsert user profile after social sign-in', error);
  }
}
